<template>
  <div class="flip-memory-game">
    <div class="fmg-header">
      <h2 class="fmg-title"><i class="el-icon-star-on"></i> 翻翻乐（记忆配对）</h2>
      <p class="fmg-desc">
        先看记忆倒计时内记住所有小动物位置；记忆结束后会短暂亮牌再盖上。翻牌时须在「翻牌时限」内翻出第二张，两张相同则消除，任意一对翻错或超时则整局重来（同一套图案重新洗牌）。
      </p>
    </div>

    <div class="fmg-settings card-block">
      <div class="fmg-row">
        <span class="fmg-label">总格数</span>
        <el-select v-model="totalCells" size="small" class="fmg-select" :disabled="gameActive">
          <el-option :value="8" label="8 格（4 对）" />
          <el-option :value="12" label="12 格（6 对）" />
          <el-option :value="16" label="16 格（8 对）" />
          <el-option :value="20" label="20 格（10 对）" />
        </el-select>
      </div>
      <div class="fmg-row">
        <span class="fmg-label">记忆倒计时（秒）</span>
        <el-input-number v-model="memorySeconds" class="fmg-input-num" :min="5" :max="180" :step="5" size="small" :disabled="gameActive" />
      </div>
      <div class="fmg-row">
        <span class="fmg-label">翻牌时限（秒）</span>
        <span class="fmg-hint">翻出第一张后，须在此时间内翻出第二张</span>
        <el-input-number v-model="flipPairSeconds" class="fmg-input-num" :min="2" :max="60" :step="1" size="small" :disabled="gameActive" />
      </div>
      <div class="fmg-actions">
        <el-button v-if="!gameActive" type="primary" class="fmg-btn-main" icon="el-icon-video-play" @click="startGame">开始游戏</el-button>
        <el-button v-else type="warning" class="fmg-btn-main" plain icon="el-icon-refresh-left" @click="abortToSetup">结束并返回设置</el-button>
      </div>
    </div>

    <div v-if="gameActive" class="fmg-board-wrap card-block">
      <div class="fmg-status">
        <template v-if="phase === 'memorizing'">
          <span class="fmg-phase-tag">记忆阶段</span>
          <span class="fmg-countdown">剩余 {{ memoryLeft }} 秒</span>
        </template>
        <template v-else-if="phase === 'peek'">
          <span class="fmg-phase-tag">再看一眼</span>
        </template>
        <template v-else-if="phase === 'playing'">
          <span class="fmg-phase-tag">翻牌中</span>
          <span v-if="firstIndex !== null && flipLeft > 0" class="fmg-countdown fmg-flip-timer">本步剩余 {{ flipLeft }} 秒</span>
          <span class="fmg-steps">步数：{{ steps }}</span>
        </template>
        <template v-else-if="phase === 'won'">
          <span class="fmg-won"><i class="el-icon-success"></i> 全部配对成功！</span>
          <el-button type="primary" class="fmg-btn-again" size="small" @click="playAgain">再玩一局</el-button>
        </template>
      </div>

      <div
        class="fmg-grid"
        :style="{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }"
      >
        <!-- 不用 button[disabled]：浏览器会给禁用按钮整体降透明/变灰，彩色 emoji 会发虚 -->
        <div
          v-for="(c, i) in cards"
          :key="c.uid"
          role="button"
          class="fmg-cell"
          :class="{
            'is-back': !faceVisible(i),
            'is-matched': c.matched,
            'is-disabled': phase === 'playing' && !canClickCell(i),
            'is-clickable': canClickCell(i)
          }"
          :tabindex="canClickCell(i) ? 0 : -1"
          :aria-disabled="!canClickCell(i) ? 'true' : 'false'"
          @click="onCellClick(i)"
          @keydown.enter.prevent="onCellClick(i)"
          @keydown.space.prevent="onCellClick(i)"
        >
          <span class="fmg-emoji" aria-hidden="true">{{ c.emoji }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** 随机小动物 emoji 池（不少于 10 对所需） */
const ANIMAL_EMOJIS = [
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
  '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆',
  '🦉', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐢',
  '🐍', '🦎', '🐙', '🦑', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳'
]

function shuffle(arr) {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

let uidSeq = 0

export default {
  name: 'FlipMemoryGame',
  data() {
    return {
      totalCells: 12,
      memorySeconds: 30,
      flipPairSeconds: 8,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      gameActive: false,
      phase: 'setup', // memorizing | peek | playing | won
      cards: [],
      memoryLeft: 0,
      flipLeft: 0,
      steps: 0,
      firstIndex: null,
      secondIndex: null,
      memoryTimerId: null,
      flipTimerId: null,
      flipIntervalId: null,
      peekTimeoutId: null,
      compareTimeoutId: null
    }
  },
  computed: {
    gridCols() {
      const n = this.totalCells
      const w = this.windowWidth
      /* 窄屏减少列数，格子更大、更好点 */
      if (w <= 480) {
        if (n === 8) return 2
        if (n === 12) return 3
        if (n === 20) return 4
        return 4
      }
      if (w <= 600) {
        if (n === 20) return 4
      }
      if (n === 20) return 5
      return 4
    }
  },
  mounted() {
    this._fmgResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._fmgResize, { passive: true })
  },
  beforeDestroy() {
    if (this._fmgResize) window.removeEventListener('resize', this._fmgResize)
    this.clearAllTimers()
  },
  methods: {
    clearAllTimers() {
      if (this.memoryTimerId) clearInterval(this.memoryTimerId)
      if (this.flipTimerId) clearTimeout(this.flipTimerId)
      if (this.flipIntervalId) clearInterval(this.flipIntervalId)
      if (this.peekTimeoutId) clearTimeout(this.peekTimeoutId)
      if (this.compareTimeoutId) clearTimeout(this.compareTimeoutId)
      this.memoryTimerId = null
      this.flipTimerId = null
      this.flipIntervalId = null
      this.peekTimeoutId = null
      this.compareTimeoutId = null
    },
    buildCards() {
      const pairs = this.totalCells / 2
      const picked = shuffle(ANIMAL_EMOJIS).slice(0, pairs)
      const emojis = shuffle([...picked, ...picked])
      this.cards = emojis.map((emoji) => ({
        uid: ++uidSeq,
        emoji,
        matched: false,
        revealed: true
      }))
    },
    faceVisible(index) {
      const c = this.cards[index]
      if (!c) return false
      if (this.phase === 'memorizing' || this.phase === 'peek') return true
      if (this.phase === 'won') return true
      return c.matched || c.revealed
    },
    canClickCell(index) {
      if (!this.gameActive || this.phase !== 'playing') return false
      const c = this.cards[index]
      if (!c || c.matched) return false
      if (this.firstIndex !== null && this.secondIndex !== null) return false
      if (this.firstIndex === index) return false
      if (c.revealed) return false
      return true
    },
    startGame() {
      this.clearAllTimers()
      this.gameActive = true
      this.steps = 0
      this.firstIndex = null
      this.secondIndex = null
      this.buildCards()
      this.phase = 'memorizing'
      this.memoryLeft = Math.max(5, parseInt(this.memorySeconds, 10) || 30)
      this.memoryTimerId = setInterval(() => {
        this.memoryLeft -= 1
        if (this.memoryLeft <= 0) {
          clearInterval(this.memoryTimerId)
          this.memoryTimerId = null
          this.beginPeek()
        }
      }, 1000)
    },
    beginPeek() {
      this.phase = 'peek'
      this.cards.forEach((c) => { c.revealed = true })
      this.peekTimeoutId = setTimeout(() => {
        this.peekTimeoutId = null
        this.cards.forEach((c) => {
          if (!c.matched) c.revealed = false
        })
        this.phase = 'playing'
      }, 500)
    },
    clearFlipCountdown() {
      if (this.flipTimerId) {
        clearTimeout(this.flipTimerId)
        this.flipTimerId = null
      }
      if (this.flipIntervalId) {
        clearInterval(this.flipIntervalId)
        this.flipIntervalId = null
      }
      this.flipLeft = 0
    },
    startFlipCountdown() {
      this.clearFlipCountdown()
      const sec = Math.max(2, parseInt(this.flipPairSeconds, 10) || 8)
      this.flipLeft = sec
      this.flipIntervalId = setInterval(() => {
        this.flipLeft -= 1
        if (this.flipLeft <= 0) {
          clearInterval(this.flipIntervalId)
          this.flipIntervalId = null
        }
      }, 1000)
      this.flipTimerId = setTimeout(() => {
        this.flipTimerId = null
        this.clearFlipCountdown()
        this.$message.warning('翻牌超时，重头开始')
        this.fullRestartSameSet()
      }, sec * 1000)
    },
    onCellClick(index) {
      if (!this.canClickCell(index)) return
      const c = this.cards[index]
      c.revealed = true

      if (this.firstIndex === null) {
        this.firstIndex = index
        this.startFlipCountdown()
        return
      }

      if (this.firstIndex === index) return

      this.secondIndex = index
      this.clearFlipCountdown()
      this.steps += 1

      const i1 = this.firstIndex
      const i2 = this.secondIndex
      const e1 = this.cards[i1].emoji
      const e2 = this.cards[i2].emoji

      if (e1 === e2) {
        this.cards[i1].matched = true
        this.cards[i2].matched = true
        this.firstIndex = null
        this.secondIndex = null
        if (this.cards.every((x) => x.matched)) {
          this.phase = 'won'
          this.cards.forEach((x) => { x.revealed = true })
        }
      } else {
        this.compareTimeoutId = setTimeout(() => {
          this.compareTimeoutId = null
          this.$message.error('配对错误，重头开始')
          this.fullRestartSameSet()
        }, 650)
      }
    },
    /** 同一套 emoji（ multiset ）重新洗牌，从记忆阶段重来 */
    fullRestartSameSet() {
      this.clearAllTimers()
      if (this.compareTimeoutId) {
        clearTimeout(this.compareTimeoutId)
        this.compareTimeoutId = null
      }
      if (this.peekTimeoutId) {
        clearTimeout(this.peekTimeoutId)
        this.peekTimeoutId = null
      }
      const emojis = shuffle(this.cards.map((c) => c.emoji))
      this.cards = emojis.map((emoji) => ({
        uid: ++uidSeq,
        emoji,
        matched: false,
        revealed: true
      }))
      this.firstIndex = null
      this.secondIndex = null
      this.steps = 0
      this.phase = 'memorizing'
      this.memoryLeft = Math.max(5, parseInt(this.memorySeconds, 10) || 30)
      this.memoryTimerId = setInterval(() => {
        this.memoryLeft -= 1
        if (this.memoryLeft <= 0) {
          clearInterval(this.memoryTimerId)
          this.memoryTimerId = null
          this.beginPeek()
        }
      }, 1000)
    },
    abortToSetup() {
      this.clearAllTimers()
      if (this.compareTimeoutId) {
        clearTimeout(this.compareTimeoutId)
        this.compareTimeoutId = null
      }
      this.gameActive = false
      this.phase = 'setup'
      this.cards = []
      this.firstIndex = null
      this.secondIndex = null
    },
    playAgain() {
      this.clearAllTimers()
      this.phase = 'setup'
      this.cards = []
      this.firstIndex = null
      this.secondIndex = null
      this.startGame()
    }
  }
}
</script>

<style scoped>
.flip-memory-game {
  max-width: 920px;
  margin: 0 auto;
  padding: 16px 20px 48px;
  /* 避免继承 #app.dark-mode 的浅灰 color，否则 Windows 上彩色 emoji 会像蒙灰、发虚 */
  color: #303133;
}
.fmg-header {
  margin-bottom: 20px;
}
.fmg-title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
  color: #303133;
}
.fmg-title i {
  color: #e6a23c;
  margin-right: 8px;
}
.fmg-desc {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}
.card-block {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.fmg-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.fmg-label {
  min-width: 120px;
  font-size: 14px;
  color: #606266;
}
.fmg-hint {
  font-size: 12px;
  color: #909399;
  flex: 1 1 200px;
}
.fmg-actions {
  margin-top: 8px;
}
.fmg-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  min-height: 36px;
}
.fmg-phase-tag {
  background: #ecf5ff;
  color: #409eff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
}
.fmg-countdown {
  font-size: 18px;
  font-weight: 600;
  color: #f56c6c;
}
.fmg-flip-timer {
  color: #e6a23c;
}
.fmg-steps {
  font-size: 14px;
  color: #909399;
}
.fmg-won {
  font-size: 18px;
  color: #67c23a;
  font-weight: 600;
}
.fmg-won i {
  margin-right: 6px;
}
.fmg-grid {
  display: grid;
  gap: 10px;
  width: 100%;
}
.fmg-cell {
  aspect-ratio: 1;
  border: none;
  border-radius: 10px;
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  padding: 0;
  min-height: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(230, 162, 60, 0.45);
  color: #141414;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.fmg-cell.is-clickable {
  cursor: pointer;
}
.fmg-cell.is-clickable:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(230, 162, 60, 0.25);
}
.fmg-cell:focus {
  outline: none;
}
.fmg-cell.is-clickable:focus-visible {
  outline: 3px solid #409eff;
  outline-offset: 2px;
}
.fmg-cell.is-back .fmg-emoji {
  opacity: 0;
  transform: scale(0.3);
}
.fmg-cell.is-back {
  background: linear-gradient(145deg, #409eff, #66b1ff);
  cursor: pointer;
}
.fmg-cell.is-back::after {
  content: '?';
  position: absolute;
  font-size: clamp(28px, 8vw, 40px);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
}
.fmg-cell {
  position: relative;
}
.fmg-emoji {
  /* 尽量用系统彩色 emoji 字体；不要用 antialiased（会让彩色 glyph 发灰） */
  font-family: "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Twemoji Mozilla",
    "Android Emoji", emoji, sans-serif;
  font-size: clamp(60px, 21vw, 118px);
  line-height: 1;
  transition: opacity 0.2s ease, transform 0.2s ease;
  display: block;
  margin: 0;
  transform-origin: center center;
  opacity: 1;
  filter: none;
  -webkit-font-smoothing: auto;
  font-synthesis: none;
  text-shadow: none;
  /* 明确前景色，阻断父级浅灰继承导致 emoji 褪色（尤其 Edge/Chrome + 暗黑主题站点） */
  color: #0a0a0a !important;
  forced-color-adjust: none;
}
.fmg-cell:not(.is-back) .fmg-emoji {
  opacity: 1;
  transform: scale(1.12);
}
.fmg-cell.is-matched {
  background: linear-gradient(145deg, #e1f3d8, #f0f9eb);
  cursor: default;
  box-shadow: inset 0 0 0 2px #67c23a;
}
.fmg-cell.is-disabled:not(.is-matched) {
  cursor: default;
  /* 不再整格变淡，避免翻牌阶段误伤已翻开图案；用轻微灰边表示不可点即可 */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.fmg-select {
  width: 120px;
}
.fmg-actions .fmg-btn-main {
  min-height: 40px;
}

/* ========== 移动端 / 窄屏 ========== */
@media screen and (max-width: 576px) {
  .flip-memory-game {
    max-width: 100%;
    box-sizing: border-box;
    padding-left: max(12px, env(safe-area-inset-left, 12px));
    padding-right: max(12px, env(safe-area-inset-right, 12px));
    padding-top: max(10px, env(safe-area-inset-top, 10px));
    padding-bottom: max(28px, env(safe-area-inset-bottom, 28px));
  }
  .fmg-header {
    margin-bottom: 14px;
  }
  .fmg-title {
    font-size: 18px;
    line-height: 1.35;
  }
  .fmg-desc {
    font-size: 13px;
    line-height: 1.55;
  }
  .card-block {
    padding: 12px 14px;
    margin-bottom: 12px;
    border-radius: 10px;
  }
  .fmg-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 14px;
  }
  .fmg-label {
    min-width: 0;
    width: 100%;
    font-size: 13px;
    font-weight: 500;
  }
  .fmg-hint {
    flex: none;
    width: 100%;
    font-size: 12px;
    line-height: 1.45;
    order: 0;
  }
  .fmg-row .fmg-hint {
    margin-top: -4px;
  }
  .fmg-select {
    width: 100% !important;
  }
  ::v-deep .fmg-select .el-input {
    width: 100%;
  }
  ::v-deep .fmg-input-num {
    width: 100% !important;
  }
  ::v-deep .fmg-input-num .el-input-number {
    width: 100%;
  }
  ::v-deep .fmg-input-num .el-input-number .el-input__inner {
    text-align: center;
  }
  .fmg-actions {
    margin-top: 4px;
  }
  .fmg-actions .fmg-btn-main {
    width: 100%;
    min-height: 46px;
    font-size: 16px;
  }
  .fmg-status {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }
  .fmg-countdown {
    font-size: 22px;
  }
  .fmg-phase-tag {
    font-size: 12px;
  }
  .fmg-won {
    font-size: 16px;
    width: 100%;
  }
  .fmg-btn-again {
    width: 100%;
    min-height: 44px;
    margin-top: 4px;
    font-size: 15px;
  }
  .fmg-grid {
    gap: 6px;
  }
  .fmg-cell {
    border-radius: 8px;
    touch-action: manipulation;
    -webkit-touch-callout: none;
  }
  .fmg-cell.is-clickable:active {
    transform: scale(0.97);
    opacity: 0.95;
  }
  /* 小屏 emoji：按视口宽度缩放，避免 20 格时过大溢出 */
  .fmg-emoji {
    font-size: clamp(28px, min(16vw, 12vmin), 76px);
  }
  .fmg-cell:not(.is-back) .fmg-emoji {
    transform: scale(1.05);
  }
  .fmg-cell.is-back::after {
    font-size: clamp(22px, 7vw, 34px);
  }
}

/* 触屏设备上取消 hover 放大，避免「粘住」 */
@media screen and (max-width: 576px) and (hover: none) {
  .fmg-cell.is-clickable:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

/* 超窄屏再收紧间距 */
@media screen and (max-width: 360px) {
  .fmg-grid {
    gap: 4px;
  }
  .card-block {
    padding: 10px 10px;
  }
}
</style>

<style>
.dark-mode-body .flip-memory-game .fmg-title {
  color: var(--text-primary, #e0e0e0);
}
.dark-mode-body .flip-memory-game .fmg-desc {
  color: var(--text-secondary, #a0a0a0);
}
.dark-mode-body .flip-memory-game .card-block {
  background: var(--bg-secondary, #2d2d2d);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.dark-mode-body .flip-memory-game .fmg-label {
  color: var(--text-regular, #c0c0c0);
}
.dark-mode-body .flip-memory-game .fmg-hint {
  color: var(--text-secondary, #909399);
}
.dark-mode-body .flip-memory-game .fmg-steps {
  color: var(--text-secondary, #909399);
}
/* 暗黑模式下牌格仍用白底 + 深色前景，彩色 emoji 才不会跟灰字糊在一起 */
.dark-mode-body .flip-memory-game .fmg-board-wrap {
  background: #2a2a2a !important;
}
.dark-mode-body .flip-memory-game .fmg-cell:not(.is-back) {
  background: #ffffff !important;
  border-color: rgba(230, 162, 60, 0.55) !important;
  color: #0a0a0a !important;
}
.dark-mode-body .flip-memory-game .fmg-emoji {
  color: #0a0a0a !important;
}
.dark-mode-body .flip-memory-game .fmg-cell.is-matched {
  background: linear-gradient(145deg, #e8f5e9, #f1f8e9) !important;
}
</style>
